import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Share2, X, Link2, Check, MessageCircle, Linkedin, Facebook, Send } from 'lucide-react';

// Public pages that have prerendered per-page OG (see scripts/og-routes.js).
// On these routes we expose a share widget that hands out the ".html" link,
// which social crawlers unfurl with the correct per-page preview.
const SHARE_ROUTES = new Set([
  '/',
  '/pricing',
  '/e-ticket',
  '/global-market-connect',
  '/partners',
  '/beds24',
  '/contact',
  '/einvoice/product',
  '/staymii-hr',
  '/certified-by-mysti',
  '/press/staymii-globaltix-mou-nice-2026',
  '/press/staymii-mysti-certified',
  '/press/ihcc-eticketing-partnership',
  '/press/beds24-e-invoice-soft-launch',
]);

const SITE = 'https://staymii.com';

export default function FloatingShare() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const clean = pathname.replace(/\/$/, '') || '/';
  if (!SHARE_ROUTES.has(clean)) return null;

  // Preview-friendly exact-file link (root stays clean).
  const shareUrl = clean === '/' ? `${SITE}/` : `${SITE}${clean}.html`;
  const enc = encodeURIComponent(shareUrl);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this share link:', shareUrl);
    }
  };

  const item = 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors w-full';

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-2" data-testid="floating-share">
      {open && (
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-2 w-56 animate-in" data-testid="floating-share-menu">
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">Share this page</div>
          <button onClick={copy} className={item} data-testid="floating-share-copy">
            {copied ? <><Check className="w-4 h-4 text-emerald-600" /> Link copied!</> : <><Link2 className="w-4 h-4" /> Copy link</>}
          </button>
          <a href={`https://wa.me/?text=${enc}`} target="_blank" rel="noopener noreferrer" className={item} data-testid="floating-share-whatsapp">
            <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
          </a>
          <a href={`https://t.me/share/url?url=${enc}`} target="_blank" rel="noopener noreferrer" className={item} data-testid="floating-share-telegram">
            <Send className="w-4 h-4 text-sky-500" /> Telegram
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`} target="_blank" rel="noopener noreferrer" className={item} data-testid="floating-share-linkedin">
            <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc}`} target="_blank" rel="noopener noreferrer" className={item} data-testid="floating-share-facebook">
            <Facebook className="w-4 h-4 text-blue-600" /> Facebook
          </a>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Share this page"
        data-testid="floating-share-toggle"
        className="h-12 w-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-xl flex items-center justify-center transition-colors"
      >
        {open ? <X className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
      </button>
    </div>
  );
}
